import { gql, makeVar, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { useCurrent } from '@/hooks/useCurrent';
import { useAnalyticsLogger } from '@/imports/ui/amplitude/helpers';

import {
  UseAutoFollowSponsor_CreateUserOrganizationLink,
  UseAutoFollowSponsor_CreateUserOrganizationLinkVariables,
} from './graphql-types/UseAutoFollowSponsor_CreateUserOrganizationLink';

const CREATE_FOLLOW = gql`
  mutation UseAutoFollowSponsor_CreateUserOrganizationLink(
    $input: CreateUserOrganizationLinkInput!
  ) {
    createUserOrganizationLink(input: $input) {
      data {
        _id
        organization {
          _id
          numFollowers
        }
      }
    }
  }
`;

const calledVar = makeVar(false);

export const useAutoFollowSponsor = (options: {
  validRoutes: string[];
}): void => {
  const toast = useToast();
  const [createFollow] = useMutation<
    UseAutoFollowSponsor_CreateUserOrganizationLink,
    UseAutoFollowSponsor_CreateUserOrganizationLinkVariables
  >(CREATE_FOLLOW);
  const { validRoutes } = options;
  const router = useRouter();

  const analyticsClient = useAnalyticsLogger();

  const isValidRoute = validRoutes.reduce(
    (prev, route) => prev || router.asPath.includes(route),
    false
  );
  const current = useCurrent();
  React.useEffect(() => {
    const organizationId = router.query.followSponsor;
    if (
      !router.isReady ||
      !organizationId ||
      !isValidRoute ||
      !current.user?._id ||
      calledVar() ||
      typeof organizationId !== 'string'
    ) {
      return;
    }

    calledVar(true);
    createFollow({
      variables: {
        input: { data: { userId: current.user._id, organizationId } },
      },
    })
      .then(() => {
        toast({
          status: 'success',
          description: 'Successfully followed sponsor',
        });
        analyticsClient.logUserFollowedSponsor();
      })
      .catch(() =>
        toast({
          status: 'error',
          description: 'An error occurred while following sponsor',
        })
      )
      .finally(() => {
        const { followSponsor, ...queryWithoutFollowSponsor } = router.query;
        router.replace({ query: queryWithoutFollowSponsor });
      });
  }, [
    isValidRoute,
    createFollow,
    router,
    analyticsClient,
    current.user?._id,
    toast,
  ]);
};
